import React, { useMemo } from 'react';
import { AnnotationType } from '../../api/protosCompiled/annotation/annotation_pb';
import { getAnnotationMeasurements } from './annotationMeasurements';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';

interface MeasurementOverlayProps {
  type: AnnotationType | undefined | null;
  geometry: any;
  transform?: string;
  color?: string;
}

const toNum = (v: any, fallback = 0): number => {
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : fallback;
};

const getRectWH = (g: any) => ({
  x: toNum(g?.x),
  y: toNum(g?.y),
  width: Math.max(0, toNum(g?.width)),
  height: Math.max(0, toNum(g?.height)),
});

const getCircle = (g: any) => ({
  cx: toNum(g?.cx),
  cy: toNum(g?.cy),
  r: Math.max(0, toNum(g?.r)),
});

const getEllipse = (g: any) => ({
  cx: toNum(g?.cx),
  cy: toNum(g?.cy),
  rx: Math.max(0, toNum(g?.rx)),
  ry: Math.max(0, toNum(g?.ry)),
});

const getLine = (g: any) => ({
  x1: toNum(g?.x1),
  y1: toNum(g?.y1),
  x2: toNum(g?.x2),
  y2: toNum(g?.y2),
});

const getPoints = (g: any): Array<{ x: number; y: number }> => {
  const str = String(g?.points || '').trim();
  if (!str) return [];
  return str
    .split(' ')
    .map(p => p.split(','))
    .map(([x, y]) => ({ x: toNum(x), y: toNum(y) }));
};

const centerOf = (type: AnnotationType | undefined | null, g: any): { x: number; y: number } => {
  switch (type) {
    case AnnotationType.CIRCLE: {
      const c = getCircle(g);
      return { x: c.cx, y: c.cy };
    }
    case AnnotationType.ELLIPSE: {
      const e = getEllipse(g);
      return { x: e.cx, y: e.cy };
    }
    case AnnotationType.LINE:
    case AnnotationType.ARROW: {
      const l = getLine(g);
      return { x: (l.x1 + l.x2) / 2, y: (l.y1 + l.y2) / 2 };
    }
    case AnnotationType.POLYLINE:
    case AnnotationType.POLYGON: {
      const pts = getPoints(g);
      if (pts.length === 0) return { x: 0, y: 0 };
      const sx = pts.reduce((a, p) => a + p.x, 0);
      const sy = pts.reduce((a, p) => a + p.y, 0);
      return { x: sx / pts.length, y: sy / pts.length };
    }
    default: {
      const r = getRectWH(g);
      return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
    }
  }
};

const getAnnotationTypeName = (type: AnnotationType | undefined | null): string => {
  switch (type) {
    case AnnotationType.TEXT:
      return 'text';
    case AnnotationType.RECTANGLE:
      return 'rectangle';
    case AnnotationType.CIRCLE:
      return 'circle';
    case AnnotationType.ELLIPSE:
      return 'ellipse';
    case AnnotationType.LINE:
      return 'line';
    case AnnotationType.POLYLINE:
      return 'polyline';
    case AnnotationType.POLYGON:
      return 'polygon';
    case AnnotationType.HIGHLIGHTER:
      return 'highlighter';
    case AnnotationType.ARROW:
      return 'arrow';
    case AnnotationType.HYPERLINK:
      return 'hyperlink';
    default:
      return 'unknown';
  }
};

export const MeasurementOverlay: React.FC<MeasurementOverlayProps> = ({
  type,
  geometry,
  transform,
  color,
}) => {
  const userScale = useStore(useUnityBuildStore).userScale;

  const measurement = useMemo(() => {
    if (type === undefined || type === null) return null;

    // Get real-world measurements using the new measurement system
    const measurements = getAnnotationMeasurements(getAnnotationTypeName(type), geometry, {
      precision: 1,
      scale: userScale,
    });

    switch (type) {
      case AnnotationType.RECTANGLE:
      case AnnotationType.TEXT:
      case AnnotationType.HIGHLIGHTER: {
        const r = getRectWH(geometry);
        return {
          labelTop: `W ${measurements.width}`,
          labelRight: `H ${measurements.height}`,
          area: `A ${measurements.area}`,
          perimeter: `P ${measurements.perimeter}`,
          anchor: { x: r.x, y: r.y },
          rect: r,
        } as any;
      }
      case AnnotationType.CIRCLE: {
        const c = getCircle(geometry);
        // Position at 45-degree angle from center (positive x,y direction)
        const angle45 = Math.PI / 4; // 45 degrees in radians
        const offset = c.r + 20; // Distance from circle edge
        const anchorX = c.cx + Math.cos(angle45) * offset;
        const anchorY = c.cy - Math.sin(angle45) * offset; // Negative Y because SVG Y increases downward
        return {
          label: `R ${measurements.radius}`,
          subLabel: `A ${measurements.area}`,
          subLabel2: `P ${measurements.perimeter}`,
          anchor: { x: anchorX, y: anchorY },
        } as any;
      }
      case AnnotationType.ELLIPSE: {
        const e = getEllipse(geometry);
        // Position at 45-degree angle from center (positive x,y direction)
        const angle45 = Math.PI / 4; // 45 degrees in radians
        // Use average of rx and ry for offset calculation
        const avgRadius = (e.rx + e.ry) / 2;
        const offset = avgRadius + 20; // Distance from ellipse edge
        const anchorX = e.cx + Math.cos(angle45) * offset;
        const anchorY = e.cy - Math.sin(angle45) * offset; // Negative Y because SVG Y increases downward
        return {
          label: `Rx ${measurements.radiusX} | Ry ${measurements.radiusY}`,
          subLabel: `A ${measurements.area}`,
          subLabel2: `P ${measurements.perimeter}`,
          anchor: { x: anchorX, y: anchorY },
        } as any;
      }
      case AnnotationType.LINE:
      case AnnotationType.ARROW: {
        const l = getLine(geometry);
        return {
          label: `L ${measurements.length}`,
          anchor: { x: (l.x1 + l.x2) / 2, y: (l.y1 + l.y2) / 2 - 6 },
        } as any;
      }
      case AnnotationType.POLYLINE: {
        const c = centerOf(type, geometry);
        return {
          label: `L ${measurements.length}`,
          anchor: { x: c.x, y: c.y - 6 },
        } as any;
      }
      case AnnotationType.POLYGON: {
        const c = centerOf(type, geometry);
        return {
          label: `P ${measurements.perimeter}`,
          subLabel: `A ${measurements.area}`,
          anchor: { x: c.x, y: c.y },
        } as any;
      }
      default: {
        return null;
      }
    }
  }, [type, geometry, userScale]);

  const stroke = color || '#1976d2';
  const fill = 'rgba(255,255,255,0.95)';
  const strokeColor = 'rgba(0,0,0,0.15)';

  if (!measurement) return null;

  // Render minimal, readable labels. Avoid heavy dimension lines for now.
  if (
    (type === AnnotationType.RECTANGLE ||
      type === AnnotationType.TEXT ||
      type === AnnotationType.HIGHLIGHTER) &&
    measurement.rect
  ) {
    const r = measurement.rect as { x: number; y: number; width: number; height: number };
    const topX = r.x + r.width / 2;
    const topY = r.y - 8;
    const rightX = r.x + r.width + 6;
    const rightY = r.y + r.height / 2;
    const areaX = r.x + r.width / 2;
    const areaY = r.y + r.height / 2;

    // Calculate dynamic background sizes based on text content
    const topTextWidth = Math.max((measurement.labelTop as string).length * 7, 40);
    const rightTextWidth = Math.max((measurement.labelRight as string).length * 7, 40);
    const areaTextWidth = Math.max((measurement.area as string).length * 6, 50);
    const perimeterTextWidth = Math.max((measurement.perimeter as string).length * 6, 50);

    return (
      <g transform={transform} style={{ pointerEvents: 'none' }}>
        {/* Width label with background */}
        <rect
          x={topX - topTextWidth / 2 - 4}
          y={topY - 12}
          width={topTextWidth + 8}
          height={16}
          rx={3}
          ry={3}
          fill={fill}
          stroke={strokeColor}
          strokeWidth={1}
        />
        <text x={topX} y={topY} textAnchor="middle" fontSize={12} fill={stroke} fontWeight="500">
          {measurement.labelTop}
        </text>

        {/* Height label with background */}
        <rect
          x={rightX - 4}
          y={rightY - 8}
          width={rightTextWidth + 8}
          height={16}
          rx={3}
          ry={3}
          fill={fill}
          stroke={strokeColor}
          strokeWidth={1}
        />
        <text
          x={rightX + 4}
          y={rightY + 2}
          textAnchor="start"
          fontSize={12}
          fill={stroke}
          fontWeight="500"
        >
          {measurement.labelRight}
        </text>

        {/* Area label with background */}
        <rect
          x={areaX - areaTextWidth / 2 - 4}
          y={areaY - 8}
          width={areaTextWidth + 8}
          height={16}
          rx={3}
          ry={3}
          fill={fill}
          stroke={strokeColor}
          strokeWidth={1}
        />
        <text
          x={areaX}
          y={areaY + 2}
          textAnchor="middle"
          fontSize={11}
          fill={stroke}
          fontWeight="500"
        >
          {measurement.area}
        </text>

        {/* Perimeter label with background */}
        <rect
          x={areaX - perimeterTextWidth / 2 - 4}
          y={areaY + 12}
          width={perimeterTextWidth + 8}
          height={16}
          rx={3}
          ry={3}
          fill={fill}
          stroke={strokeColor}
          strokeWidth={1}
        />
        <text
          x={areaX}
          y={areaY + 22}
          textAnchor="middle"
          fontSize={11}
          fill={stroke}
          fontWeight="500"
        >
          {measurement.perimeter}
        </text>
      </g>
    );
  }

  const anchor = (measurement as any).anchor as { x: number; y: number } | undefined;
  if (!anchor) return null;

  // Special handling for ellipse and circle measurements to improve visibility
  if (type === AnnotationType.ELLIPSE || type === AnnotationType.CIRCLE) {
    const label = (measurement as any).label as string;
    const subLabel = (measurement as any).subLabel as string;
    const subLabel2 = (measurement as any).subLabel2 as string;

    // Calculate text width for better background sizing
    const labelWidth = Math.max(label.length * 7, 60); // Approximate character width
    const subLabelWidth = subLabel ? Math.max(subLabel.length * 6, 50) : 0;
    const subLabel2Width = subLabel2 ? Math.max(subLabel2.length * 6, 50) : 0;
    const maxWidth = Math.max(labelWidth, subLabelWidth, subLabel2Width);
    const hasMultipleLabels = Boolean(subLabel && subLabel2);

    return (
      <g transform={transform} style={{ pointerEvents: 'none' }}>
        {/* Enhanced background with stroke for better visibility */}
        <rect
          x={anchor.x - maxWidth / 2 - 6}
          y={anchor.y - 18}
          width={maxWidth + 12}
          height={hasMultipleLabels ? 50 : subLabel ? 36 : 20}
          rx={4}
          ry={4}
          fill={fill}
          stroke={strokeColor}
          strokeWidth={1}
        />
        <text
          x={anchor.x}
          y={anchor.y - 2}
          textAnchor="middle"
          fontSize={12}
          fill={stroke}
          fontWeight="500"
        >
          {label}
        </text>
        {subLabel && (
          <text
            x={anchor.x}
            y={anchor.y + 12}
            textAnchor="middle"
            fontSize={11}
            fill={stroke}
            fontWeight="500"
          >
            {subLabel}
          </text>
        )}
        {subLabel2 && (
          <text
            x={anchor.x}
            y={anchor.y + 24}
            textAnchor="middle"
            fontSize={11}
            fill={stroke}
            fontWeight="500"
          >
            {subLabel2}
          </text>
        )}
      </g>
    );
  }

  // Default case for lines, polylines, polygons, etc.
  const label = (measurement as any).label as string;
  const subLabel = (measurement as any).subLabel as string;

  // Calculate dynamic background sizing
  const labelWidth = Math.max(label.length * 7, 40);
  const subLabelWidth = subLabel ? Math.max(subLabel.length * 6, 40) : 0;
  const maxWidth = Math.max(labelWidth, subLabelWidth);
  const hasSubLabel = Boolean(subLabel);

  return (
    <g transform={transform} style={{ pointerEvents: 'none' }}>
      <rect
        x={anchor.x - maxWidth / 2 - 4}
        y={anchor.y - (hasSubLabel ? 16 : 8)}
        width={maxWidth + 8}
        height={hasSubLabel ? 32 : 16}
        rx={3}
        ry={3}
        fill={fill}
        stroke={strokeColor}
        strokeWidth={1}
      />
      <text
        x={anchor.x}
        y={anchor.y + 1}
        textAnchor="middle"
        fontSize={12}
        fill={stroke}
        fontWeight="500"
      >
        {label}
      </text>
      {subLabel && (
        <text
          x={anchor.x}
          y={anchor.y + 14}
          textAnchor="middle"
          fontSize={11}
          fill={stroke}
          fontWeight="500"
        >
          {subLabel}
        </text>
      )}
    </g>
  );
};

export default MeasurementOverlay;
